import React, { useCallback, useMemo, useState } from "react";
import { Snackbar, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import { amber, green } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import SnackbarContent from "@mui/material/SnackbarContent";
import WarningIcon from "@mui/icons-material/Warning";
import { Theme } from "@mui/material/styles";

const snackbarContext = React.createContext({});

export const SnackbarProvider = (props: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [variant, setVariant] = useState<keyof typeof variantIcon>("info");

  const openSnackbar = useCallback((message: string, variant: keyof typeof variantIcon) => {
    setIsOpen(true);
    setMessage(message);
    setVariant(variant);
  }, []);

  const closeSnackbar = useCallback(() => {
    setIsOpen(false);
  }, []);

  const { children } = props;
  const contextValue = useMemo(
    () => ({
      openSnackbar,
      closeSnackbar,
      snackbarIsOpen: isOpen,
      message,
    }),
    [openSnackbar, closeSnackbar, isOpen, message]
  );

  return (
    <snackbarContext.Provider
      value={contextValue}
    >
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={isOpen}
        autoHideDuration={6000}
        onClose={closeSnackbar}
      >
        <LogiSnackbar
          onClose={closeSnackbar}
          variant={variant}
          message={message}
        />
      </Snackbar>
      {children}
    </snackbarContext.Provider>
  );
};

export function useSnackbar(): any {
  const context = React.useContext(snackbarContext);
  if (context === undefined) {
    throw new Error(`useSnackbar must be used within a Provider`);
  }
  return context;
}

/*
Copied from Material-UI example
*/
const variantIcon = {
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoIcon,
};

const iconStyles = {
  fontSize: 20,
};

const iconVariantStyles = {
  opacity: 0.9,
  mr: 1,
};

export interface LogiSnackbarProps {
  className?: string;
  message?: string;
  onClose?: () => void;
  variant: keyof typeof variantIcon;
}

function LogiSnackbar(props: LogiSnackbarProps) {
  const { className, message, onClose, variant, ...other } = props;
  const Icon = variantIcon[variant];

  const getVariantStyles = (theme: Theme) => {
    switch (variant) {
      case 'success':
        return { backgroundColor: green[600] };
      case 'error':
        return { backgroundColor: theme.palette.error.dark };
      case 'info':
        return { backgroundColor: theme.palette.primary.main };
      case 'warning':
        return { backgroundColor: amber[700] };
      default:
        return {};
    }
  };

  return (
    <SnackbarContent
      sx={(theme) => getVariantStyles(theme)}
      className={className}
      aria-describedby="client-snackbar"
      message={
        <Box
          component="span"
          id="client-snackbar"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Icon sx={{ ...iconStyles, ...iconVariantStyles }} />
          {message}
        </Box>
      }
      action={[
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={onClose}
          size="large">
          <CloseIcon sx={iconStyles} />
        </IconButton>,
      ]}
      {...other}
    />
  );
}
